import {Injectable} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {ModalController, NavController} from '@ionic/angular/standalone';
import {defer, filter, firstValueFrom, Observable, of, take} from 'rxjs';

import {
  CollegamentoAccountModalComponent,
  CollegamentoEsito,
  COLLEGAMENTO_ESITI,
} from '../../shared/modals/collegamento-account-modal/collegamento-account-modal.component';
import {PathEnum} from '../../shared/enums/path-enum';
import {LocalStorageService, StorageProperties} from '../local-storage.service';
import {SpidAuthService} from '../spid-auth.service';
import {TokenExpiryService} from '../token-expiry.service';
import {
  classificaErroreCollegamento,
  CollegamentoAccountApiService,
  CollegamentoErrore,
  CollegamentoStatoResponse,
} from './collegamento-account.api';
import {leggiClaimsIdentitaTemporanea, providerDaAlias} from './collegamento-account.util';

/** Contenuto dello slot dedicato in sessionStorage (chiave `collegamento_pending`). */
export interface CollegamentoPendingSlot {
  access_token: string;
  refresh_token: string | null;
  /** Alias dell'IdP dal claim `identity_provider`: serve per rifare il login con lo stesso IdP. */
  alias: string | null;
}

/**
 * Orchestra il collegamento di un'identità SPID/CIE a un account esistente (email + password).
 *
 * Quando il BE risponde 409 `collegamento_account_richiesto` il token appena ottenuto NON è una
 * sessione: viene spostato in uno slot dedicato e lo slot standard viene svuotato, così AuthGuard,
 * interceptor, error reporting e push non vedono nessuna sessione. Il token resta disponibile solo
 * per le tre chiamate pubbliche di collegamento (token nel body, mai in `Authorization`).
 *
 * La modale è presentata su `/login` PRIMA di qualunque navigazione che la chiuderebbe
 * (`ModalGuard`): si naviga a `/login` per primi e poi la si presenta.
 */
@Injectable({providedIn: 'root'})
export class CollegamentoAccountService {
  private modaleAperta = false;

  /** Promessa dell'ultimo esito gestito (chiusura modale + pulizia + navigazione). Per i test. */
  attesaEsito: Promise<void> = Promise.resolve();

  constructor(
    private readonly localStorageService: LocalStorageService,
    private readonly tokenExpiryService: TokenExpiryService,
    private readonly spidAuthService: SpidAuthService,
    private readonly api: CollegamentoAccountApiService,
    private readonly modalController: ModalController,
    private readonly navCtrl: NavController,
    private readonly router: Router,
  ) {}

  /** Esiste un'identità in attesa di collegamento (ad esempio dopo un reload della pagina). */
  haSlotPending(): boolean {
    return this.leggiSlot() !== null;
  }

  /**
   * Sposta i token dell'identità corrente nello slot dedicato, svuota la sessione standard e
   * presenta la modale. Da chiamare quando `refreshUserData$` ha dato `CollegamentoRichiestoError`.
   */
  async avviaDaSessioneCorrente(): Promise<void> {
    const accessToken = this.localStorageService.getAccessToken();
    if (!accessToken) {
      // Nessun token da spostare: non c'è niente da collegare. Si rientra al login senza modale.
      await this.navCtrl.navigateRoot(PathEnum.LOGIN, {replaceUrl: true});
      return;
    }
    const refreshToken = this.localStorageService.getRefreshToken() ?? null;
    const alias = leggiClaimsIdentitaTemporanea(accessToken).alias ?? null;

    this.tokenExpiryService.stopMonitoring();
    // Prima si svuota lo slot standard (che pulisce anche lo slot pending), poi si scrive il
    // pending: l'ordine inverso lo cancellerebbe.
    await firstValueFrom(this.localStorageService.cleanPreferenceToken$());
    const slot: CollegamentoPendingSlot = {access_token: accessToken, refresh_token: refreshToken, alias};
    sessionStorage.setItem(StorageProperties.COLLEGAMENTO_PENDING, JSON.stringify(slot));

    await this.navCtrl.navigateRoot(PathEnum.LOGIN, {replaceUrl: true});
    await this.presenta(slot);
  }

  /**
   * App-init: se dopo un reload esiste lo slot pending, richiama /stato e ripresenta la modale.
   * L'osservabile completa subito dopo aver pianificato la presentazione, che attende il primo
   * `NavigationEnd` (a APP_INITIALIZER in corso la UI non c'è ancora): l'avvio non resta bloccato
   * né dalla rete né dalla modale.
   */
  riprendi$(): Observable<void> {
    return defer(() => {
      const slot = this.leggiSlot();
      if (!slot) {
        return of(undefined);
      }
      // Una sessione standard valida ha la precedenza: lo slot è un residuo e va scartato.
      if (this.localStorageService.getAccessToken()) {
        sessionStorage.removeItem(StorageProperties.COLLEGAMENTO_PENDING);
        return of(undefined);
      }
      const pronto$ = this.router.navigated
        ? of(undefined)
        : this.router.events.pipe(filter((e) => e instanceof NavigationEnd), take(1));
      void firstValueFrom(pronto$, {defaultValue: undefined})
        .then(() => this.presenta(slot))
        .catch((e) => console.warn('[Collegamento] ripresa non riuscita', e));
      return of(undefined);
    });
  }

  private leggiSlot(): CollegamentoPendingSlot | null {
    const raw = sessionStorage.getItem(StorageProperties.COLLEGAMENTO_PENDING);
    if (!raw) {
      return null;
    }
    try {
      const parsed = JSON.parse(raw) as Partial<CollegamentoPendingSlot> | null;
      if (parsed && typeof parsed.access_token === 'string' && parsed.access_token) {
        return {
          access_token: parsed.access_token,
          refresh_token: typeof parsed.refresh_token === 'string' ? parsed.refresh_token : null,
          alias: typeof parsed.alias === 'string' ? parsed.alias : null,
        };
      }
    } catch {
      // slot corrotto: si scarta sotto
    }
    sessionStorage.removeItem(StorageProperties.COLLEGAMENTO_PENDING);
    return null;
  }

  /** Richiama /stato e presenta la modale; resolve a modale presentata (non alla chiusura). */
  private async presenta(slot: CollegamentoPendingSlot): Promise<void> {
    if (this.modaleAperta) {
      return;
    }
    this.modaleAperta = true;
    try {
      const claims = leggiClaimsIdentitaTemporanea(slot.access_token);
      let stato: CollegamentoStatoResponse | null = null;
      let erroreStato: CollegamentoErrore | null = null;
      try {
        stato = await firstValueFrom(this.api.stato(slot.access_token));
      } catch (e) {
        erroreStato = classificaErroreCollegamento(e);
      }

      const modal = await this.modalController.create({
        component: CollegamentoAccountModalComponent,
        componentProps: {
          // Impedisce a `AutocloseOverlaysService` di chiuderla con la navigazione/back.
          isNotDismissableWithBack: true,
          accessToken: slot.access_token,
          providerIniziale: providerDaAlias(slot.alias),
          stato,
          erroreStato,
          nomeIniziale: claims.nome ?? '',
          cognomeIniziale: claims.cognome ?? '',
          emailIniziale: claims.email ?? '',
          telefonoIniziale: claims.telefono ?? '',
        },
        backdropDismiss: false,
        // Gesture, backdrop ed Esc non chiudono: si esce solo con i ruoli espliciti della modale.
        canDismiss: async (_data?: unknown, role?: string) =>
          COLLEGAMENTO_ESITI.includes(role as CollegamentoEsito),
        htmlAttributes: {
          // Id con prefisso `collegamento-`: niente conflitti con le altre modali che usano `modal-title`.
          'aria-labelledby': 'collegamento-titolo',
          'aria-describedby': 'collegamento-descrizione',
        },
      });
      await modal.present();

      this.attesaEsito = modal
        .onDidDismiss()
        .then(({role}) => this.eseguiEsito(role as CollegamentoEsito | undefined, slot.alias))
        .catch((e) => console.error('[Collegamento] gestione esito non riuscita', e))
        .finally(() => {
          this.modaleAperta = false;
        });
    } catch (e) {
      this.modaleAperta = false;
      throw e;
    }
  }

  /**
   * Azioni di uscita. In tutte la pulizia locale (compreso lo slot pending) precede la
   * navigazione a /login, altrimenti `NoAuthGuard` o un reload riproporrebbero la modale.
   * - `esci` / `sessioneScaduta`: chiude anche la sessione SSO Keycloak (dispositivi condivisi).
   * - `login` / `riaccedi` (dopo il collegamento): l'utente temporaneo è già cancellato, quindi
   *   NIENTE signoutRedirect: solo pulizia locale, compreso l'utente OIDC di oidc-client-ts.
   */
  private async eseguiEsito(esito: CollegamentoEsito | undefined, alias: string | null): Promise<void> {
    await firstValueFrom(this.localStorageService.cleanPreferenceToken$());

    if (esito === 'login' || esito === 'riaccedi') {
      await this.spidAuthService.rimuoviUtenteLocale();
      if (esito === 'riaccedi') {
        this.spidAuthService.login(alias ?? undefined);
        return;
      }
      await this.navCtrl.navigateRoot(PathEnum.LOGIN, {replaceUrl: true, state: {loggingOut: true}});
      this.focalizzaPaginaLogin();
      return;
    }

    await this.navCtrl.navigateRoot(PathEnum.LOGIN, {replaceUrl: true, state: {loggingOut: true}});
    this.focalizzaPaginaLogin();
    await this.spidAuthService.logout();
  }

  /**
   * Niente `restoreFocus` al trigger: la modale si apre da sola (nessun elemento l'ha attivata),
   * quindi alla chiusura il focus finirebbe su `<body>`. Lo si porta sul titolo (h1) della pagina
   * di accesso, reso focalizzabile da codice, solo se il focus non è già altrove.
   */
  private focalizzaPaginaLogin(): void {
    setTimeout(() => {
      const attivo = document.activeElement;
      if (attivo && attivo !== document.body) {
        return;
      }
      const titolo = Array.from(document.querySelectorAll<HTMLElement>('ion-router-outlet h1')).find(
        (h) => h.offsetParent !== null,
      );
      if (!titolo) {
        return;
      }
      if (!titolo.hasAttribute('tabindex')) {
        titolo.setAttribute('tabindex', '-1');
      }
      titolo.focus();
    });
  }
}
